// -------------------------------------------
// Z-index settings
// -------------------------------------------

import {
	COMMISSION_ATTACHMENT_TYPE_NAME,
	COMMISSION_PAYMENT_METHOD_NAME,
	COMMISSION_REPORTING_PERIOD_NAME,
	COMMISSION_TYPE_NAME,
	POS_TYPE,
	REPORT_TYPE,
} from '../constants';
import getPOSGeneralName from './machine/getPOSGeneralName';

export const zIndex = {
	INTERACTION_DROPDOWN: 999,					// Want dropdown items to render on top of nearly all things
	PLANOGRAM_ITEM_SOLD_OUT_IMAGE: 11,
	PLANOGRAM_ITEM_DEX_MODIFY_OVERLAY: 10,
	PLANOGRAM_ITEM_DEX_DETAILS: 12,
	PLANOGRAM_ITEM_FOOTER: 9,
	PLANOGRAM_ITEM_DELETE_ICON: 8,				// Delete icon renders over overlay
	PLANOGRAM_ITEM_OVERLAY: 7,
	PLANOGRAM_ITEM_SELECTION: 9,
};


// -------------------------------------------
// Alerts
// -------------------------------------------

// Hours parameter:
// No sales > X hours
// No communication > X hours
// No service > X days
// Probable note jam - no note activity > X hours
// Probable coin jam - no note activity > X hours

// Vendouts parameter
// Vendouts - vendouts above X

// Price parameter
// Dex price is not divisible by X (5 cents)
// Dex price above X (e.g. $80)
// Dex price below X (e.g. 90c)

// Temperature parameter - from DEX
// Temperature above or below X & Y
// !!Note - only with two parameter potentially?

// No parameter - show immediately if condition === true
// Open door
// Price mismatch
// Sale on disabled selection with dex selection disabled

// Consider if might want to apply different alert to some other category of things,
// E.g. a certain client



export const alertTypes = [
	{ label: 'No sales', value: 1 },
	{ label: 'No communication', value: 2 },
	{ label: 'No service', value: 3 },
	{ label: 'Vendouts', value: 4 },
	{ label: 'Open door', value: 5 },
	{ label: 'Temperature outside range', value: 6 },
	{ label: 'Price mismatch', value: 7 },
	{ label: 'Price not divisible', value: 8 },
	{ label: 'Price above', value: 9 },
	{ label: 'Price below', value: 10 },
	{ label: 'Probable note jam', value: 11 },
	{ label: 'Probably coin jam', value: 12 },
	{ label: 'Sale on disabled selection', value: 13 },
];

// -------------------------------------------
// Coin mechs
// -------------------------------------------

export const coinMechCapacities = {
	default: { // CF7906 // CF7000
		10: 78,
		20: 112,
		50: 29,
		100: 39,
		200: 49,
	},
};

export const DEFAULT_TUBE_REFILL_LEVEL = 50; // 50 percent

// -------------------------------------------
// Commissions
// -------------------------------------------

export const commissionTypes = Object.keys(COMMISSION_TYPE_NAME).map(id => ({
	label: COMMISSION_TYPE_NAME[id],
	value: Number(id),
}));

export const commissionReportingPeriods = Object.keys(COMMISSION_REPORTING_PERIOD_NAME).map(id => ({
	label: COMMISSION_REPORTING_PERIOD_NAME[id],
	value: id,
}));

export const commissionPaymentMethods = Object.keys(COMMISSION_PAYMENT_METHOD_NAME).map(id => ({
	label: COMMISSION_PAYMENT_METHOD_NAME[id],
	value: id,
}));

export const commissionAttachmentTypes = Object.keys(COMMISSION_ATTACHMENT_TYPE_NAME).map(id => ({
	label: COMMISSION_ATTACHMENT_TYPE_NAME[id],
	value: id,
}));

// might be machine/market/POS
// POS is used in MapView
export const posAttributeHints = (posType, hintLabel, tenantSettings) => {
	let posName = getPOSGeneralName({ tenantSettings, lowercase: true });

	switch (posType) {
		case POS_TYPE.MARKET:
			posName = 'market';
			break;
		case POS_TYPE.MACHINE:
			posName = 'machine';
			break;
	}

	switch (hintLabel) {
		case 'VENDOUTS':
			return `Vendouts - the number of selections in the ${posName} that are currently sold out`;
		case 'PERCENTAGE_FULL':
			return `Percentage full - how full the ${posName} is as a percentage of total parlevel`;
		case 'SALES_SINCE_LAST_RESTOCK':
			return `The total sales in dollars the  ${posName} has made since the last restock`;
		case 'VENDS_SINCE_LAST_RESTOCK':
			return `The total vends the ${posName} has made since the last restock`;
		case 'DSL_CASHLESS':
			return `The number of days since last cashless sale. This value will reset when a ${posName}'s location or place is changed`;
		default: return null;
	}
};

export const machineAttributeHints = {
	VENDOUTS_PREDICTION:
		'Vendouts prediction - a prediction of the number of vendouts in 24 hours time, inferred from sales volume over the last 30 days',
	VENDOUTS_PREDICTION_SKIP_WEEKENDS:
		'Vendouts prediction - a prediction of the number of vendouts in 24 hours time or next work day, inferred from sales volume over the last 30 days',
	MISSED_SALES_PREDICTION:
		'Missed sales prediction - a prediction of missed sales due to vend outs in 24 hours time, inferred from sales volume over the last 30 days',
	MISSED_SALES_PREDICTION_SKIP_WEEKENDS:
		'Missed sales prediction - a prediction of missed sales due to vend outs in 24 hours time or next work day, inferred from sales volume over the last 30 days',
	REPORTED_PRICE_MISMATCH_COUNT:
		'Reported price mismatch count: the number of enabled planogram selections where the price set in Sentry does not match the price reported in the last communication',
	NEXT_SERVICE_PRICE_CHANGES_COUNT:
		'Next service price changes: the number of enabled planogram selections with a price change queued to be applied at the next service',
	SALES_LAST_7_DAYS:
		'Sales made in the last 7 days, including credit card surcharges',
	DAILY_AVERAGE_SALES:
		'Daily average sales, based on the last 7 days of sales, including credit card surcharges',
	CREATED:
		'When the machine was added to the system',
	TICKETS:
		'Currently open tickets',
	DSLS:
		'Days since last service',
	// Machine sales view
	DAILY_AVERAGE_SALES_7_DAYS:
		'Daily average sales, based on the last 7 days of sales, including credit card surcharges',
	DAILY_AVERAGE_SALES_30_DAYS:
		'Daily average sales, based on the last 30 days of sales, including credit card surcharges',
	DAILY_AVERAGE_SALES_91_DAYS:
		'Daily average sales, based on the last 90 days of sales, including credit card surcharges',
	DAILY_AVERAGE_SALES_365_DAYS:
		'Daily average sales, based on the last 365 days of sales, including credit card surcharges',
	WEEKLY_AVERAGE_SALES_7_DAYS:
		'Weekly average sales, based on the last 7 days of sales, including credit card surcharges',
	WEEKLY_AVERAGE_SALES_30_DAYS:
		'Weekly average sales, based on the last 30 days of sales, including credit card surcharges',
	WEEKLY_AVERAGE_SALES_91_DAYS:
		'Weekly average sales, based on the last 90 days of sales, including credit card surcharges',
	WEEKLY_AVERAGE_SALES_365_DAYS:
		'Weekly average sales, based on the last 365 days of sales, including credit card surcharges',

};

export const clientAttributeHints = {
	NAME: '',
	ADDRESS: '',
};


// -------------------------------------------
// Service runs
// -------------------------------------------

export const MANUAL_RESTOCK_WARNING = 'Warning! Only use manual restock if pressing the restock button on the machine does not work or machine doesn\'t receive restock communication. Please wait a few minutes after pressing the machine restock button. If the service has still not registered, then use manual restock.';

// -------------------------------------------
// Service dockets
// -------------------------------------------

export const serviceDocketInventoryCheckStatus = {
	PENDING: 'Pending',
	REQUESTED: 'Not confirmed',
	CONFIRMED: 'Confirmed',
	CONFIRMED_WITH_CHANGES: 'Confirmed (changes)',
};

// -------------------------------------------
// Planograms
// -------------------------------------------

export const DEFAULT_NEW_EMPTY_PLANOGRAM = {
	template: false,
	name: '',
	description: '',
	items: [],
};

export const DEFAULT_NEW_PLANOGRAM_ITEM = {
	enabled: true,
	quantity: 0,
	parlevel: 8,
	price: 200,
	selection: '10',
	row: 1,
	column: 1,
};

export const DEFAULT_NEW_INGREDIENT_PLANOGRAM_ITEM = {
	enabled: true,
	quantity: 0,
	parlevel: 1000,
	row: 1,
	column: 0,
	isIngredient: true,
};

const numSelections = 60;	// Number of selections
const offset = 10; 			// Start numbering the selections from here
const skipSelections = [15, 16, 17, 18, 19, 25, 26, 27, 28, 29]; 	// Skip these selections

export const DEFAULT_NEW_PLANOGRAM = [

	...new Array(numSelections),

].map((item, index) => {
	// Create the initial selections
	return index + offset;
}).filter(selection => {
	// Filter out the skipped selections
	return !skipSelections.includes(selection);
}).map(selection => {
	return {
		...DEFAULT_NEW_PLANOGRAM_ITEM,
		selection,
		// Chop up the selection to get row & columns, then return them to ints
		column: parseInt(selection.toString()[1], 10),
		row: parseInt(selection.toString()[0], 10),

		/*
		Regarding the ID:
		id's must be unique for editing, and selections are unique, so
		this assignment is sensible.

		We cast the id to a string because:

		1. 	When planogram is supplied from the back end for planograms already
			in Mongo, the id supplied is a string, so this is more consistent.

		2.	We sometimes check for equality between IDs, e.g. when bulk editing
			planogram items in the creator and when items are in the 'selected'
			state, they are represented as an object's keys, and therefore cast
			to a string there. So, for comparison purposes, it's best here to
			cast this to a string.

		*/
		id: selection.toString(),
	};
});

// normal planogram which has ingredients has only 1 element
export const DEFAULT_NEW_PLANOGRAM_WITH_INGREDIENTS = [{
	...DEFAULT_NEW_PLANOGRAM_ITEM,
	selection: '1',
	// Chop up the selection to get row & columns, then return them to ints
	column: 1,
	row: 0,
	id: '1',
}];

export const DEFAULT_NEW_INGREDIENT_PLANOGRAM =	['1', '2', '3', '4', '5', '6', '7', '8', '9'] // 9  items by default
	.map((selection, column) => {
		return {
			...DEFAULT_NEW_INGREDIENT_PLANOGRAM_ITEM,
			selection,
			// Chop up the selection to get row & columns, then return them to ints
			column,
			row: 1,
			id: selection.toString(),
		};
	});

// The number of row items created when a new planogram row is created
export const DEFAULT_NEW_ROW_ITEMS = 5;

// -------------------------------------------
// List version constants
// -------------------------------------------

// LocationList

export const locationListVersions = {
	// The primary location list
	LOCATION_LIST_VIEW: {
		controls: true,
		name: true,
		address: true,
		client: true,
		machines: true,
		person: true,
		secondaryPerson: true,
	},
	CLIENT_DETAIL_VIEW: {
	// The version on ClientDetail, filtered by locations at an client
		name: true,
		address: true,
		machines: true,
	},
};

// Sold Product List Versions
export const soldProductListVersions = {
	DEFAULT_VIEW: {
		shouldShowBulkModify: false,
		controls: false,
	},
};

export const clientListVersions = {
	// The primary client list
	CLIENT_LIST_VIEW: {
		controls: true,
		name: true,
		address: true,
		locations: true,
		person: true,
		secondaryPerson: true,
		commission: true,
		created: true,
	},
};

/*

Since version.versionField resolves to false if versionField is not defined,
for planogram purposes, only define the fields that you want to show as true.
Otherwise, the version fields will default to false.

*/

export const planogramDetailVersions = {
	// The primary planogram view, used when viewing machine's planograms
	DEFAULT_VIEW: {
		controls: true,
		inlineSettingsControls: true,
		copy: true,
		toggleDex: true,
		showReportedPriceMismatch: true,
		showReportedPriceOptions: true,
		showHealthCompliance: true,
		setMachinePriesFromDex: true,
		dropdownMenu: true,
		isDefaultView: true,
		modifyLayout: true,

		canDragItems: true,
		allowToAddIngredientPlanogram: true,
		dynamicParlevels: true,
		editablePlanogramItem: true,
		showSalesData: true,
		showPlanogramNotes: true,
		showDUNE: true,
	},
	// The version in MachinePreview.jsx - unable to be edited
	PREVIEW_VIEW: {
		fitToScreen: true,
		showTooltipOnItemHover: true,
		showReportedPriceMismatch: true,
		editablePlanogramItem: false,
		allowToAddIngredientPlanogram: false,
	},
	DESTROYED_PREVIEW_VIEW: {
		fitToScreen: true,
		showTooltipOnItemHover: true,
		showReportedPriceMismatch: true,
		editablePlanogramItem: false,
		allowToAddIngredientPlanogram: false,
		includeDestroyedItems: true,
	},
	READ_ONLY_VIEW: {
		showTooltipOnItemHover: true,
		showReportedPriceMismatch: true,
		editablePlanogramItem: false,
		allowToAddIngredientPlanogram: false,
		includeDestroyedItems: true,
		showSalesData: true,
		showPlanogramNotes: false,
		showDUNE: true,
	},
	DYNAMIC_PARLEVELS_PREVIEW: {
		fitToScreen: true,
		showTooltipOnItemHover: true,
		showReportedPriceMismatch: true,
		editablePlanogramItem: false,
		allowToAddIngredientPlanogram: false,
		showParlevelPreview: true,
	},
	// The creator version of planogram detail
	CREATOR_VIEW: {
		isTemplatePlanogram: true,
		isCreator: true,
		controls: true,
		inlineSettingsControls: true,
		showHealthCompliance: true,

		// You can modify layout in the creator view
		modifyLayout: true,

		// Special version property, that only sets the product selector in
		// PlanogramEditPanel to use Onselect.
		productSelectorWithOnSelect: true,
		dropdownMenu: true,

		canDragItems: true,
		// if modifyPlanogram used sequentially then it fails if not awaited (setState is async)
		asychronousModifyFunction: true,
		allowToAddIngredientPlanogram: true,
		editablePlanogramItem: true,
	},
	// The template version that you apply to other machines
	TEMPLATE_VIEW: {
		isTemplatePlanogram: true,
		controls: true,
		inlineSettingsControls: true,
		copy: true,
		showHealthCompliance: true,
		modifyLayout: true,
		information: true, // The template view show planogram information
		dropdownMenu: true,
		showProductChangeType: true, // used for master planograms
		showPriceChangeType: true,
		canDragItems: true,
		allowToAddIngredientPlanogram: true,
		editablePlanogramItem: true,
	},
	SIMULATOR_VIEW: {
		showReportedPriceMismatch: false,
		editablePlanogramItem: false,
		isSimulator: true, // simulator specific options
		allowToAddIngredientPlanogram: false,
	},
	ASSET_TEMPLATE_VIEW: {
		isAssetTemplate: true,
		isTemplatePlanogram: true,
		inlineSettingsControls: true,
		editablePlanogramItem: true,
		hideSelection: true,
		hideQuantity: true,
		hidePrice: true,
		hideSoldOuts: true,
		controls: true,
		hideViewChangeToggle: true,
		dropdownMenu: true,
	},
	ASSET_TEMPLATE_CREATOR_VIEW: {
		isCreator: true,
		isAssetTemplate: true,
		isTemplatePlanogram: true,
		inlineSettingsControls: true,
		editablePlanogramItem: true,
		hideSelection: true,
		hideQuantity: true,
		hidePrice: true,
		hideSoldOuts: true,
		controls: true,
		hideViewChangeToggle: true,
		dropdownMenu: true,
	},
};

export const serviceRunStatusVersions = {
	DEFAULT_VIEW: {
		name: true,
		vendouts: true,
		percentageFull: true,
		salesSinceLastRestock: true,
		serviceTime: true,
		inventory: true,
		fillDex: true,
		options: true,
		checkbox: false,
		dropdownMenu: true,
		docket: true,
		dsls: true,
		openTicketCount: true,
		repaired: true,
		status: true,
	},
};

export const SERVICE_RUN_DETAIL_MODE = {
	EXPANDED: 1,
	COMPACT: 2,
	DEFAULT: 3,
};

export const SERVICE_RUN_DETAIL_MODE_NAMES = {
	1: 'expanded',
	2: 'compact',
	3: 'default',
};

export const serviceRunDetailStatusVersions = {
	DEFAULT_VIEW: {
		routeNameAsLink: false,
		detailMode: SERVICE_RUN_DETAIL_MODE.DEFAULT,
	},
	DAILY_VIEW: {
		isDailyView: true,
		routeNameAsLink: true,
		showPhotoQuickAccessIcon: true,
		detailMode: SERVICE_RUN_DETAIL_MODE.EXPANDED,
	},
	DAILY_VIEW_COMPACT: {
		isDailyView: true,
		routeNameAsLink: true,
		showPhotoQuickAccessIcon: true,
		detailMode: SERVICE_RUN_DETAIL_MODE.COMPACT,
	},

};



export const pickListDetailVersions = {
	DEFAULT_VIEW: {
		selection: true,
		product: true,
		pickOrder: true,
		price: true,
		quantityParlevel: true,
		unadjustedPickAmount: true,
		finalPickAmount: true,
		checkbox: false,
	},
};

export const pickListDetailSummaryVersions = {
	DEFAULT_VIEW: {
		product: true,
		pickAmount: true,
		pickOrder: true,
		checkbox: false,
		extraQuantity: true,
	},
};

export const serviceRunDocketsVersions = {
	DEFAULT_VIEW: {
		checkbox: false,
	},
};

export const serviceDocketListVersions = {
	DEFAULT_VIEW: {
		checkbox: false,
		driver: true,
		date: true,
		machine: false,		// Doesn't have machine as default is browing by machine
		status: true,
		manualRestock: true,
		vendouts: true,
		percentageFull: true,
		amountSold: true,
		dsls: true,
		options: true,
	},
	DRIVER_VIEW: {
		checkbox: false,
		driver: false,		// Doesn't have driver as this view is browing by driver
		machine: true,
		date: true,
		status: true,
		manualRestock: true,
		vendouts: true,
		percentageFull: true,
		amountSold: true,
		dsls: true,
	},
};

export const serviceDocketDetailVersions = {
	DEFAULT_VIEW: {
		serviceStatus: true,
	},
};

export const nativePlanogramVersions = {

	PICKLIST_VIEW: {
		pickList: true,
		filterRequiredQuantity: true,
		hideImages: true,
		showAllSelections: true,
		batchProducts: true,
		hideHealthCompliance: true,
		hidePickedItems: true,
		settings: true,
		showSalesLastXDaysForPicklists: true,
		showPickOrderForPicklists: true,
	},
	CONFIRM_STOCK_LEVELS_VIEW: {
		filterRequiredQuantity: false,
		hideImages: true,
		showAllSelections: false,
		batchProducts: false,
		hideHealthCompliance: true,
		hidePickedItems: false,
		settings: true,
		showSalesLastXDays: true,
		showExpiryDateButtons: true,
	},
	CONFIRM_OFFLINE_VALUES: {
		filterRequiredQuantity: false,
		settings: true,
		hideImages: true,
		showAllSelections: false,
		batchProducts: false,
		hideHealthCompliance: true,
		hidePickedItems: false,
		showAdvancedView: false,
		showSalesLastXDays: true,
	},
	MACHINE_SETUP_VIEW: {
		filterRequiredQuantity: false,
		settings: false,
		hideImages: true,
		showAllSelections: false,
		batchProducts: false,
		hideHealthCompliance: true,
		hidePickedItems: false,
		readOnly: true,
	},
};

export const reportViewVersions = {

	// default report view (page)
	DEFAULT_VIEW: {
		reportType: REPORT_TYPE.MACHINES,
		showExtraFilters: true,
	},

	// when it's a machine report
	MACHINE_VIEW: {
		showExtraFilters: false,
		reportType: REPORT_TYPE.PRODUCTS,
	},
};
